import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../../services/api";
import "./CandidateSearch.css";
import dayjs from "dayjs";

const CandidateSearchBar = ({ token, weekRange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Search candidates when search term changes
  useEffect(() => {
    const searchCandidates = async () => {
      if (searchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      
      setLoading(true);
      try {
        // Get jobs for the current week
        const jobs = await get('/api/job', token);
        
        // Filter jobs for the current week
        const currentWeekJobs = jobs.filter(job => {
          const startDate = new Date(job.jobStartDate);
          const endDate = new Date(job.jobEndDate);
          const weekStartDate = new Date(weekRange.startDate);
          const weekEndDate = new Date(weekRange.endDate);
          
          return (
            (startDate <= weekEndDate && endDate >= weekStartDate) ||
            (startDate >= weekStartDate && startDate <= weekEndDate) ||
            (endDate >= weekStartDate && endDate <= weekEndDate)
          );
        });
        
        // Get all job IDs for the current week
        const jobIds = currentWeekJobs.map(job => job._id);
        
        // Get candidates
        const candidates = await get('/api/candidate', token);
        
        // Get job-candidate relationships for these jobs
        const jobCandidatesPromises = jobIds.map(jobId => 
          get(`/api/job/candidate/job/${jobId}?weekStartDate=${dayjs(weekRange.startDate).format('YYYY-MM-DD')}&weekEndDate=${dayjs(weekRange.endDate).format('YYYY-MM-DD')}`, token)
        );
        
        const jobCandidatesResponses = await Promise.all(jobCandidatesPromises);
        const jobCandidates = jobCandidatesResponses.flat();
        
        // Build results with candidate and job information
        const results = [];
        
        // Filter candidates by search term
        const filteredCandidates = candidates.filter(candidate => 
          (candidate.firstName && candidate.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (candidate.lastName && candidate.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        // Match candidates with jobs
        for (const candidate of filteredCandidates) {
          // Find job-candidate relationships for this candidate
          const candidateJobs = jobCandidates.filter(jc => jc.candidateId === candidate._id);
          
          for (const candidateJob of candidateJobs) {
            // Find job details
            const job = currentWeekJobs.find(j => j._id === candidateJob.jobId);
            
            if (job) {
              results.push({
                candidateId: candidate._id,
                candidateName: `${candidate.firstName} ${candidate.lastName}`,
                jobId: job._id,
                planName: job.jobTitle,
                planId: job.jobID,
                company: job.company
              });
            }
          }
        }
        
        // Also search for jobs that match the search term
        const filteredJobs = currentWeekJobs.filter(job => 
          (job.jobTitle && job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (job.company && job.company.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        for (const job of filteredJobs) {
          // Find candidates for this job
          const jobCandidatesList = jobCandidates.filter(jc => jc.jobId === job._id);
          
          for (const jc of jobCandidatesList) {
            // Find candidate details
            const candidate = candidates.find(c => c._id === jc.candidateId);
            
            if (candidate) {
              // Check if this combination is already in results
              const exists = results.some(r => 
                r.candidateId === candidate._id && r.jobId === job._id
              );
              
              if (!exists) {
                results.push({
                  candidateId: candidate._id,
                  candidateName: `${candidate.firstName} ${candidate.lastName}`,
                  jobId: job._id,
                  planName: job.jobTitle,
                  planId: job.jobID,
                  company: job.company
                });
              }
            }
          }
        }
        
        setSearchResults(results);
        setShowDropdown(results.length > 0);
      } catch (error) {
        console.error("Error searching candidates:", error);
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      searchCandidates();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, token, weekRange]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleResultClick = (result) => {
    // Navigate to the requirement plans page and directly open the candidates modal
    navigate(`/requirement-plans`, { 
      state: { 
        highlightCandidateId: result.candidateId,
        autoOpenModal: true,
        jobId: result.jobId,
        directModal: true, // Flag to indicate we want to directly open the modal
        candidateName: result.candidateName,
        planName: result.planName
      } 
    });
    setShowDropdown(false);
    setSearchTerm("");
  };

  return (
    <div className="candidate-search-container" style={{ position: "relative", width: "280px" }}>
      <div className="position-relative">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search candidates or plans..."
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={loading}
          style={{ height: "38px" }}
        />
        {loading && (
          <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ position: "absolute", right: "10px", top: "10px" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>

      {showDropdown && searchResults.length > 0 && (
        <div 
          ref={dropdownRef}
          className="dropdown-menu show" 
          style={{ 
            display: "block", 
            width: "100%", 
            maxHeight: "300px", 
            overflowY: "auto",
            position: "absolute",
            zIndex: 1000
          }}
        >
          {searchResults.map((result, index) => (
            <button
              key={`${result.candidateId}-${result.jobId}-${index}`}
              className="dropdown-item"
              onClick={() => handleResultClick(result)}
            >
              <div className="d-flex justify-content-between">
                <div>
                  <strong>{result.candidateName}</strong>
                </div>
                <div className="text-muted">
                  {result.planName} - {result.company}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CandidateSearchBar;
